import type { Showcase, ToolCallRecord, TourStop } from '@k4ia/shared'
import { STRINGS, type UiLocale } from './i18n-strings'

/**
 * Geführter Rundgang und Vorschlags-Chips — reine Logik, ohne Store, damit
 * sie sich prüfen lässt.
 *
 * Der Rundgang folgt der Reihenfolge aus dem Admin („Rundgang"); ist dort
 * nichts gepflegt, der Sortierreihenfolge der Showcases. Zwei Phasen je
 * Station: `walk` (K4ia beschreibt den Weg) und `arrived` (sie erklärt).
 */
export interface TourState {
  /** Showcase-IDs in Reihenfolge des Rundgangs */
  ids: string[]
  index: number
  phase: 'walk' | 'arrived'
}

/**
 * Die festen Chips, die keine Frage an K4ia sind, sondern den Rundgang
 * steuern — in der Sprache des Besuchers. `chipKind` erkennt sie in beiden
 * Sprachen wieder, damit ein Sprachwechsel mitten im Rundgang nichts bricht.
 */
export type ChipKind = 'here' | 'next' | 'end' | 'nextShowcase' | 'tour'

export function chip(kind: ChipKind, locale: UiLocale): string {
  const t = STRINGS[locale]
  switch (kind) {
    case 'here':
      return t.chipHere
    case 'next':
      return t.chipNext
    case 'end':
      return t.chipEnd
    case 'nextShowcase':
      return t.chipNextShowcase
    case 'tour':
      return t.chipTour
  }
}

export function chipKind(text: string): ChipKind | null {
  for (const t of Object.values(STRINGS)) {
    if (text === t.chipHere) return 'here'
    if (text === t.chipNext) return 'next'
    if (text === t.chipEnd) return 'end'
    if (text === t.chipNextShowcase) return 'nextShowcase'
    if (text === t.chipTour) return 'tour'
  }
  return null
}

// Deutsche Bezeichner für Tests und Aufrufer, die die Sprache nicht kennen.
export const CHIP_ICH_BIN_DA = STRINGS.de.chipHere
export const CHIP_WEITER = STRINGS.de.chipNext
export const CHIP_RUNDGANG_ENDE = STRINGS.de.chipEnd
export const CHIP_NAECHSTER = STRINGS.de.chipNextShowcase
export const CHIP_RUNDGANG_START = STRINGS.de.chipTour

export function tourOrder(stops: TourStop[], showcases: Showcase[]): string[] {
  if (stops.length > 0) return stops.map((s) => s.showcaseId)
  return [...showcases].sort((a, b) => a.sortOrder - b.sortOrder).map((s) => s.id)
}

/**
 * Welcher Showcase zu einer Antwort gehört: der aus `get_showcase` (Ausgabe
 * oder Eingabe); sonst der Kontext der Frage (Tag, Kamera, Rundgang); sonst
 * der, dessen Titel in Antwort oder Frage vorkommt — K4ia beantwortet „Erzähl
 * mir was zu SumUp" oft aus dem Prompt, ohne das Werkzeug zu rufen.
 */
export function showcaseOfAnswer(
  toolCalls: ToolCallRecord[],
  showcases: Showcase[],
  fallback: string | undefined,
  texts: string[] = [],
): string | undefined {
  for (const call of toolCalls) {
    if (call.name !== 'get_showcase') continue
    const out = call.output as { found?: boolean; showcase?: { id?: unknown } } | undefined
    if (out?.found && typeof out.showcase?.id === 'string') return out.showcase.id
    const ref = (call.input as { showcaseIdOrSlug?: unknown }).showcaseIdOrSlug
    if (typeof ref === 'string') {
      const sc = showcases.find((s) => s.id === ref || s.slug === ref)
      if (sc) return sc.id
    }
  }
  if (fallback) return fallback
  for (const text of texts) {
    const sc = showcaseInText(text, showcases)
    if (sc) return sc
  }
  return undefined
}

const FUELLWOERTER = new Set(['als', 'der', 'die', 'das', 'und', 'mit', 'im', 'in', 'the', 'of'])

/**
 * Der Showcase, dessen Titel im Text steckt: alle tragenden Wörter des Titels
 * („SumUp als POS" → sumup, pos) kommen vor. Bei mehreren gewinnt der mit den
 * meisten Wörtern — „Meta Brillen" vor einem Titel, der nur „Meta" trägt.
 */
export function showcaseInText(text: string, showcases: Showcase[]): string | undefined {
  const woerter = new Set(tokens(text))
  let bester: { id: string; treffer: number } | null = null
  for (const sc of showcases) {
    const titel = tokens(sc.title).filter((w) => !FUELLWOERTER.has(w))
    if (titel.length === 0 || !titel.every((w) => woerter.has(w))) continue
    if (!bester || titel.length > bester.treffer) bester = { id: sc.id, treffer: titel.length }
  }
  return bester?.id
}

function tokens(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .split(' ')
    .filter((w) => w.length >= 2)
}

/**
 * Vorschläge unter der letzten Antwort. Im Rundgang steuern sie den Rundgang;
 * sonst drei Fragen aus den FAQ des Showcases, die noch nicht gestellt wurden,
 * und „Nächster Showcase".
 */
export function buildSuggestions(params: {
  tour: TourState | null
  showcaseId: string | undefined
  showcases: Showcase[]
  order: string[]
  asked: string[]
  locale?: UiLocale
}): string[] {
  const { tour, showcases, order } = params
  const locale = params.locale ?? 'de'
  if (tour) {
    if (tour.phase === 'walk') return [chip('here', locale)]
    const id = tour.ids[tour.index]
    const faq = faqChips(id, showcases, params.asked, 2)
    const hatNaechste = tour.index + 1 < tour.ids.length
    return [...faq, chip(hatNaechste ? 'next' : 'end', locale)]
  }
  if (!params.showcaseId) return []
  const chips = faqChips(params.showcaseId, showcases, params.asked, 3)
  if (nextShowcaseId(params.showcaseId, order)) chips.push(chip('nextShowcase', locale))
  return chips
}

export function nextShowcaseId(current: string, order: string[]): string | null {
  const i = order.indexOf(current)
  if (i < 0) return order[0] ?? null
  return order[i + 1] ?? null
}

function faqChips(
  id: string | undefined,
  showcases: Showcase[],
  asked: string[],
  max: number,
): string[] {
  const sc = showcases.find((s) => s.id === id)
  if (!sc) return []
  const gestellt = new Set(asked.map(norm))
  return sc.faq
    .map((f) => f.question)
    .filter((q) => !gestellt.has(norm(q)))
    .slice(0, max)
}

function norm(s: string): string {
  return s.trim().toLowerCase()
}
